import BranchRequiredWrapper from "@/components/banners/BranchRequiredWrapper";
import ContextualSaveBar from "@/components/bars/ContextualSaveBar";
import DataTable, { Column } from "@/components/layouts/DataTable";
import DateFilterButton from "@/components/layouts/DateFilterButton";
import DeleteConfirmationDialog from "@/components/layouts/DeleteConfirmationDialog";
import FilterOptionsButton from "@/components/layouts/FilterOptionsButton";
import PageHeader from "@/components/layouts/PageHeader";
import SearchInput from "@/components/layouts/SearchInput";
import PageLoader from "@/components/loader/PageLoader";
import { useUser } from "@/contexts/UserContext";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import {
  faEdit,
  faSearch,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DropResult } from "@hello-pangea/dnd";
import { format } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

interface CategoryItem {
  id: number;
  name: string;
  num_sub_categories: number;
  num_products: number;
  status: string;
  created_at: string;
  photo: string;
  ranking: number;
}

interface SortState {
  field: keyof CategoryItem;
  direction: "asc" | "desc";
}

interface FilterState {
  status: string;
}

const Category = () => {
  const itemsPerPage = 25;
  const { t } = useTranslation();
  const { user } = useUser();
  const navigate = useNavigate();
  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const [categoryItems, setCategoryItems] = useState<CategoryItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortState, setSortState] = useState<SortState>({
    field: "name",
    direction: "asc",
  });
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);
  const [filterStartDate, setFilterStartDate] = useState<Date | null>(null);
  const [filterEndDate, setFilterEndDate] = useState<Date | null>(null);

  const [filterState, setFilterState] = useState<FilterState>({
    status: "",
  });

  const columns: Column<CategoryItem>[] = [
    {
      key: "name",
      header: t("back.management.menu.category.table.category"),
      sortable: true,
      bold: true,
    },
    {
      key: "num_products",
      header: t("back.management.menu.category.table.product"),
      sortable: true,
    },
    {
      key: "status",
      header: t("back.management.menu.category.table.status"),
      bold: true,
    },
    {
      key: "created_at",
      header: t("back.management.menu.category.table.date"),
      sortable: true,
    },
    {
      key: "action",
      header: t("back.management.menu.category.table.action"),
    },
  ];

  const getStatusColor = (item: CategoryItem) => {
    switch (item.status) {
      case "Active":
        return "bg-green-500";
      case "No Product":
        return "bg-yellow-500";
      case "Inactive":
        return "bg-red-500";
      case "Draft":
        return "bg-gray-500";
      default:
        return "bg-gray-500";
    }
  };

  const fetchCategoryItems = async () => {
    try {
      if (!loading && axiosInstance && user?.selectedBranch) {
        const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}${
          import.meta.env.VITE_APP_REDBIRDPOSBE_CATEGORY_INFORMATION
        }`;
        let url = `${apiUrl}?branch=${user.selectedBranch.id}&page=${currentPage}&page_size=${itemsPerPage}`;
        if (searchQuery) {
          url += `&name=${encodeURIComponent(searchQuery)}`;
        }
        if (filterStartDate) {
          const formattedStartDate = filterStartDate.toISOString();
          url += `&created_at__gt=${formattedStartDate}`;
        }
        if (filterEndDate) {
          const formattedEndDate = filterEndDate.toISOString();
          url += `&created_at__lt=${formattedEndDate}`;
        }
        if (filterState.status) {
          url += `&status=${filterState.status}`;
        }

        const response = await axiosInstance.get(url);

        const fetchedItems = response.data.results.map((item: CategoryItem) => {
          // Convert and format the 'created_at' date
          const zonedDate = toZonedTime(item.created_at, "America/Chicago");
          const formattedDate = format(zonedDate, "MM/dd/yyyy, HH:mm");
          return { ...item, created_at: formattedDate };
        });

        fetchedItems.sort((a: CategoryItem, b: CategoryItem) => {
          if (sortState.field === "created_at") {
            // Convert back to dates for comparison
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);
            return sortState.direction === "asc"
              ? dateA.getTime() - dateB.getTime()
              : dateB.getTime() - dateA.getTime();
          } else {
            const aValue = a[sortState.field];
            const bValue = b[sortState.field];
            if (typeof aValue === "number" && typeof bValue === "number") {
              return sortState.direction === "asc"
                ? aValue - bValue
                : bValue - aValue;
            } else if (
              typeof aValue === "string" &&
              typeof bValue === "string"
            ) {
              return sortState.direction === "asc"
                ? aValue.localeCompare(bValue)
                : bValue.localeCompare(aValue);
            }
          }
          return 0;
        });
        //sorting them by rank
        fetchedItems.sort(
          (a: CategoryItem, b: CategoryItem) => a.ranking - b.ranking,
        );
        setCategoryItems(fetchedItems);
        setTotalItems(response.data.count);
      }
    } catch (error) {
      console.error("Error fetching category items:", error);
    }
  };

  const handleFilterStartDateChange = (date: Date | null) => {
    setFilterStartDate(date);
  };

  const handleFilterEndDateChange = (date: Date | null) => {
    setFilterEndDate(date);
  };

  const handleFilterChange = (field: keyof FilterState, value: string) => {
    setFilterState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleRemoveFilters = () => {
    setFilterState({
      status: "",
    });
    setCurrentPage(1);
    fetchCategoryItems();
  };

  // Immediate initial fetch on component mount
  useEffect(() => {
    // Only run this on initial mount when we have the required dependencies
    if (axiosInstance !== null && axiosInstance !== undefined && user?.selectedBranch) {
      fetchCategoryItems();
    }
  }, [axiosInstance, user?.selectedBranch]); 

  // Debounced fetch only for search changes
  useEffect(() => {
    // Skip for initial load
    if (searchQuery === '' && currentPage === 1) {
      return;
    }
    
    const timerId = setTimeout(() => {
      fetchCategoryItems();
    }, 1000);

    return () => clearTimeout(timerId);
  }, [searchQuery]); 

  // Non-debounced updates for everything else
  useEffect(() => {
    // Skip the initial render
    if (currentPage === 1 && sortState.field === "name" && sortState.direction === "asc") {
      return;
    }
    fetchCategoryItems();
  }, [currentPage, sortState, filterStartDate, filterEndDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle filter state changes
  useEffect(() => {
    setCurrentPage(1);
    fetchCategoryItems();
  }, [filterState]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDeleteConfirmation = (id: number) => {
    setItemToDelete(id);
    setDeleteConfirmationOpen(true);
  };

  const closeDeleteConfirmation = () => {
    setItemToDelete(null);
    setDeleteConfirmationOpen(false);
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      try {
        if (!loading && axiosInstance) {
          const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}${
            import.meta.env.VITE_APP_REDBIRDPOSBE_CATEGORY_INFORMATION
          }`;

          await axiosInstance.delete(`${apiUrl}${itemToDelete}`);
          fetchCategoryItems();
          toast.success(t("back.management.menu.category.successdelete"));
        }
      } catch (error) {
        console.error("Error deleting menu item:", error);
        toast.error(t("back.management.menu.category.faildelete"));
      }
      closeDeleteConfirmation();
    }
  };

  const handleSortChange = (field: keyof CategoryItem) => {
    setSortState((prevState) => ({
      field,
      direction:
        prevState.field === field && prevState.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const downloadCSV = () => {
    const csvRows = [
      ["ID", "Item Name", "Description", "Price"],

      ...categoryItems.map((item) => [
        item.id,
        item.name,
        item.num_sub_categories,
        item.num_products,
        item.created_at,
        item.status,
      ]),
    ];

    const csvData = csvRows.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("hidden", "");
    a.setAttribute("href", url);
    a.setAttribute("download", "category.csv");
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const [isDirty, setIsDirty] = useState(false);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(categoryItems);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setCategoryItems(items);
    setIsDirty(true);
  };

  const handleSave = async () => {
    try {
      if (!loading && axiosInstance && user?.selectedBranch) {
        const apiUrl = `${
          import.meta.env.VITE_APP_REDBIRDPOSBE_CATEGORY_INFORMATION
        }bulk_update_rankings/`;
        const updatedRankings = categoryItems.map((category, index) => ({
          id: category.id,
          new_ranking: index,
        }));

        await axiosInstance.post(apiUrl, {
          rankings: updatedRankings,
          branch: user.selectedBranch.id,
        });

        setIsDirty(false);
        toast.success("Successfully Reordered");
      }
    } catch (error) {
      console.error("Error saving new category order:", error);
      toast.error("Failed to reorder");
    }
  };

  const handleDiscard = () => {
    fetchCategoryItems();
    setIsDirty(false);
  };

  const renderMobileCard = (item: CategoryItem) => (
    <div className="flex w-full items-center justify-between">
      <div className="flex items-center">
        {item.photo && (
          <img src={item.photo} alt={item.name} className="mr-4 h-16 w-16" />
        )}
        <div>
          <p className="font-bold text-gray-900">{item.name}</p>
          <p className="text-gray-700">
            {item.num_products}{" "}
            {t("back.management.menu.category.table.product")}
          </p>
        </div>
      </div>
      <div className="flex h-full flex-col justify-between">
        <button
          onClick={() => navigate(`/menu/categories/edit/${item.id}`)}
          className="fa-2x mb-2 text-gray-600 hover:text-gray-800"
        >
          <FontAwesomeIcon icon={faEdit} />
        </button>
        <button
          onClick={() => openDeleteConfirmation(item.id)}
          className="fa-2x text-red-600 hover:text-red-800"
        >
          <FontAwesomeIcon icon={faTrashAlt} />
        </button>
      </div>
    </div>
  );

  return (
    <PageLoader isFetching={isFetching}>
      <BranchRequiredWrapper>
        <div className="container mx-auto px-4 py-2">
          {isDirty && (
            <ContextualSaveBar
              isDirty={isDirty}
              onSave={handleSave}
              onDiscard={handleDiscard}
              isSaving={false}
            />
          )}
          <PageHeader
            title={t("back.management.menu.category.title")}
            exportLabel={t("back.management.menu.category.export")}
            addLabel={t("back.management.menu.category.addcategory")}
            onExport={downloadCSV}
            onAdd={() => navigate("/menu/categories/add")}
          />
          <DeleteConfirmationDialog
            isOpen={deleteConfirmationOpen}
            onClose={closeDeleteConfirmation}
            onConfirm={confirmDelete}
            title={t("back.management.menu.category.deleteConfirmation.title")}
            message={t(
              "back.management.menu.category.deleteConfirmation.message",
            )}
          />
          <div className="mb-4 flex flex-col justify-between space-y-4 lg:flex-row lg:items-center lg:space-y-0">
            <div className="relative mr-4 max-w-md flex-1">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400"
              />
              <SearchInput
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={t("back.management.menu.category.searchCategory")}
              />
            </div>
            <div className="relative">
              <DateFilterButton
                onStartDateChange={handleFilterStartDateChange}
                onEndDateChange={handleFilterEndDateChange}
                startDate={filterStartDate}
                endDate={filterEndDate}
                labelText={t("back.management.menu.category.selectdate")}
              />
              <FilterOptionsButton
                filterOptions={[
                  {
                    label: t("back.management.menu.category.status"),
                    value: filterState.status,
                    onChange: (value) => handleFilterChange("status", value),
                    placeholder: t(
                      "back.management.menu.category.selectStatus",
                    ),
                    type: "select",
                    options: [
                      {
                        value: "Active",
                        label: t("back.management.menu.category.active"),
                      },
                      {
                        value: "Inactive",
                        label: t("back.management.menu.category.inactive"),
                      },
                      {
                        value: "No Product",
                        label: t("back.management.menu.category.noProduct"),
                      },
                      {
                        value: "Draft",
                        label: t("back.management.menu.category.draft"),
                      },
                    ],
                  },
                ]}
                onRemoveFilters={handleRemoveFilters}
              />
            </div>
          </div>
          <DataTable
            data={categoryItems}
            columns={columns}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            totalItems={totalItems}
            sortState={sortState}
            onSortChange={handleSortChange}
            onPageChange={handlePageChange}
            onEdit={(id) => {
              console.log("this is id", id);
              console.log("this is id", `/menu/categories/edit/${id}`);
              navigate(`/menu/categories/edit/${id}`);
            }}
            onDelete={openDeleteConfirmation}
            getStatusColor={getStatusColor}
            renderMobileCard={renderMobileCard}
            onDragEnd={handleDragEnd}
          />
        </div>
      </BranchRequiredWrapper>
    </PageLoader>
  );
};

export default Category;
